import { FiAlertTriangle, FiBell, FiCheckCircle } from 'react-icons/fi'

import { browserName, devicePlatform } from 'modules/webPush'
import { blockedSteps, settingsAppName } from './guidance'
import Steps from './steps'

interface IWebPushPermissionProps {
    permission: NotificationPermission | null
    busy: boolean
    onAllow: () => void
}

/**
 * «دسترسی اعلان در این مرورگر»: the one setting the app cannot change itself.
 *
 * The app asks for it once, by itself, on the first open — and the browser
 * never shows that dialog a second time on its own. So whoever tapped «Block»,
 * or closed it without answering, would otherwise have no way back. This is
 * that way back, and it is why it lives in settings: ask again while the
 * browser still allows it, and once it does not, name the exact setting to
 * change instead of leaving a switch that does nothing.
 */
export default function WebPushPermission({
    permission,
    busy,
    onAllow,
}: IWebPushPermissionProps) {
    if (!permission) return null
    const app = settingsAppName(devicePlatform(), browserName())

    return (
        <section className="bg-white rounded-lg shadow-header p-4">
            <div className="flex items-center gap-2">
                <span className="text-text-secondary">
                    {permission === 'granted' ? (
                        <FiCheckCircle size={18} />
                    ) : permission === 'denied' ? (
                        <FiAlertTriangle size={18} />
                    ) : (
                        <FiBell size={18} />
                    )}
                </span>
                <p className="text-base font-bold">دسترسی اعلان در مرورگر</p>
            </div>

            {permission === 'granted' && (
                <p className="text-xs text-text-secondary mt-2 leading-6">
                    به همیان اجازهٔ اعلان داده‌اید. برای خاموش کردن اعلان‌ها
                    کلید بالا کافی است؛ اگر می‌خواهید خود اجازه را پس بگیرید، از
                    آیکون کنار نشانی سایت در {app}، «اعلان‌ها» را روی «مسدود»
                    بگذارید.
                </p>
            )}

            {permission === 'default' && (
                <>
                    <p className="text-xs text-text-secondary mt-2 leading-6">
                        هنوز به همیان اجازهٔ اعلان نداده‌اید. تا اجازه ندهید،
                        هیچ اعلانی در این مرورگر نمایش داده نمی‌شود.
                    </p>
                    <button
                        type="button"
                        onClick={onAllow}
                        disabled={busy}
                        className="mt-3 bg-green-1 text-white text-sm rounded-md py-2 px-4 disabled:opacity-60"
                    >
                        {busy ? 'منتظر پاسخ مرورگر…' : 'اجازه می‌دهم'}
                    </button>
                </>
            )}

            {permission === 'denied' && (
                <>
                    <p className="text-xs mt-2 leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                        اعلان‌های همیان در این مرورگر مسدود شده‌اند. مرورگر
                        دیگر خودش نمی‌پرسد، پس باید یک بار دستی اجازه بدهید:
                    </p>
                    <Steps steps={blockedSteps(devicePlatform(), app)} />
                </>
            )}
        </section>
    )
}
